import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  api,
  setTenantIdProvider,
  setUserDisabledHandler,
  setUnauthorizedHandler,
} from "../lib/apiClient";
import { AuthResponse, AuthSession, LoginPayload, UserAccount } from "../types";

interface AuthContextValue {
  token: string | null;
  user: UserAccount | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  disabledMessage: string | null;
  clearDisabledMessage: () => void;
  login: (payload: LoginPayload) => Promise<AuthResponse>;
  logout: () => void;
  refreshUser: () => Promise<UserAccount | null>;
  completePasswordChange: (response: AuthResponse) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [disabledMessage, setDisabledMessage] = useState<string | null>(null);

  const applyAuthResponse = useCallback(
    (response: AuthResponse) => {
      const nextSession: AuthSession = {
        token: null,
        user: response.user,
        sessionId: response.sessionId,
        tenantId: response.user.tenantId ?? null,
      };
      setSession(nextSession);
      return nextSession;
    },
    [],
  );

  const login = useCallback(
    async (payload: LoginPayload) => {
      const response = await api.login(payload);
      applyAuthResponse(response);
      return response;
    },
    [applyAuthResponse],
  );

  const logout = useCallback(() => {
    api.logout(null, session?.sessionId).catch(console.error);
    setSession(null);
  }, [session?.sessionId]);

  const clearDisabledMessage = useCallback(() => {
    setDisabledMessage(null);
  }, []);

  useEffect(() => {
    setUserDisabledHandler((message: string) => {
      setDisabledMessage(message);
      logout();
    });
    setUnauthorizedHandler(() => {
      logout();
    });
    return () => {
      setUserDisabledHandler(null);
      setUnauthorizedHandler(null);
    };
  }, [logout]);

  const refreshUser = useCallback(async () => {
    try {
      const user = await api.getCurrentUser(null);
      const updated: AuthSession = {
        token: null,
        user,
        sessionId: session?.sessionId,
        tenantId: session?.tenantId ?? user.tenantId ?? null,
      };
      setSession(updated);
      return user;
    } catch (error) {
      console.warn("Failed to refresh admin session", error);
      logout();
      return null;
    }
  }, [session?.tenantId, logout]);

  useEffect(() => {
    setTenantIdProvider(() => session?.tenantId ?? null);
  }, [session?.tenantId]);

  useEffect(() => {
    const verify = async () => {
      try {
        const user = await api.getCurrentUser(null, { skipAuthHandling: true });
        if (user) {
          setSession({
            token: null,
            user,
            sessionId: undefined,
            tenantId: user.tenantId ?? null,
          });
        }
      } catch {
        // No valid session — stays null
      } finally {
        setIsInitializing(false);
      }
    };
    verify();
  }, []);

  const completePasswordChange = useCallback(
    (response: AuthResponse) => {
      applyAuthResponse(response);
    },
    [applyAuthResponse],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      token: session?.token ?? null,
      user: session?.user ?? null,
      tenantId: session?.tenantId ?? null,
      isAuthenticated: Boolean(session?.user),
      isInitializing,
      disabledMessage,
      clearDisabledMessage,
      login,
      logout,
      refreshUser,
      completePasswordChange,
    }),
    [
      session,
      isInitializing,
      disabledMessage,
      clearDisabledMessage,
      login,
      logout,
      refreshUser,
      completePasswordChange,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
